import * as XLSX from "xlsx";

const TURKISH_MONTHS = {
  ocak: 1,
  subat: 2,
  mart: 3,
  nisan: 4,
  mayis: 5,
  haziran: 6,
  temmuz: 7,
  agustos: 8,
  eylul: 9,
  ekim: 10,
  kasim: 11,
  aralik: 12
};

const MONTH_LABELS = [
  "", "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"
];

export const normalizeFinancialText = (value) =>
  String(value ?? "")
    .trim()
    .toLocaleLowerCase("tr-TR")
    .replace(/[ıİ]/g, "i")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const numberValue = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (value === null || value === undefined || value === "") return 0;

  let text = String(value).trim().replace(/[^0-9,.-]/g, "");
  if (!text) return 0;

  const comma = text.lastIndexOf(",");
  const dot = text.lastIndexOf(".");
  if (comma > dot) text = text.replace(/\./g, "").replace(",", ".");
  else if (dot > comma && comma >= 0) text = text.replace(/,/g, "");
  else if (comma >= 0) text = text.replace(",", ".");

  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : 0;
};

const cleanCell = (value) => typeof value === "string" ? value.trim() : value;

const rowContains = (row, term) => {
  const normalizedTerm = normalizeFinancialText(term);
  return row.some((cell) => normalizeFinancialText(cell).includes(normalizedTerm));
};

const findRow = (rows, term, startIndex = 0) =>
  rows.findIndex((row, index) => index >= startIndex && rowContains(row, term));

const findCellIndex = (row, term, exact = false) => {
  const normalizedTerm = normalizeFinancialText(term);
  return row.findIndex((cell) => {
    const normalized = normalizeFinancialText(cell);
    return exact ? normalized === normalizedTerm : normalized.includes(normalizedTerm);
  });
};

const parseProjectIdentity = (value) => {
  const label = String(value || "").trim();
  const codeMatch = label.match(/STB\s*Kodu\s*:?\s*(\d+)/i);
  const projectCode = codeMatch?.[1] || "";
  const projectName = label
    .replace(/STB\s*Kodu\s*:?\s*\d+\s*[-–—]?\s*/i, "")
    .trim();
  return { projectCode, projectName: projectName || label, sourceLabel: label };
};

export const periodFromText = (value) => {
  const normalized = normalizeFinancialText(value);
  const yearMatch = normalized.match(/\b(20\d{2})\b/);
  if (!yearMatch) return null;

  const monthEntry = Object.entries(TURKISH_MONTHS).find(([name]) => normalized.includes(name));
  const numericMonth = normalized.match(/(?:^|\s)(0?[1-9]|1[0-2])(?:\s|$)/)?.[1];
  const month = monthEntry?.[1] || Number(numericMonth || 0);
  const year = Number(yearMatch[1]);
  if (!month || month < 1 || month > 12) return null;

  return {
    year,
    month,
    periodKey: `${year}-${String(month).padStart(2, "0")}`,
    periodLabel: `${MONTH_LABELS[month]} ${year}`
  };
};

const detectPeriod = (sheetName, fileName, rows) => {
  const fromText = periodFromText(sheetName) || periodFromText(fileName);
  if (fromText) return fromText;

  for (const row of rows.slice(0, 5)) {
    for (const cell of row) {
      if (cell instanceof Date && !Number.isNaN(cell.getTime())) {
        const month = cell.getMonth() + 1;
        const year = cell.getFullYear();
        return {
          year,
          month,
          periodKey: `${year}-${String(month).padStart(2, "0")}`,
          periodLabel: `${MONTH_LABELS[month]} ${year}`
        };
      }
    }
  }

  return null;
};

const parseProjectRows = (rows) => {
  const headerIndex = rows.findIndex((row) => rowContains(row, "stb kodu"));
  if (headerIndex < 0) return [];

  const headerRow = rows[headerIndex];
  const groupRow = rows[Math.max(0, headerIndex - 1)] || [];
  const projectColumn = findCellIndex(headerRow, "stb kodu");
  const statusColumn = findCellIndex(headerRow, "proje durumu");
  const expenseStart = findCellIndex(groupRow, "proje gider");
  const revenueStart = Math.max(projectColumn + 1, 0);
  const resolvedExpenseStart = expenseStart > revenueStart
    ? expenseStart
    : headerRow.findIndex((cell, index) => index > revenueStart && normalizeFinancialText(cell).includes("personel gider"));

  const findHeaderColumn = (term, start, end = headerRow.length) => {
    const normalizedTerm = normalizeFinancialText(term);
    for (let index = Math.max(0, start); index < Math.min(end, headerRow.length); index += 1) {
      if (normalizeFinancialText(headerRow[index]).includes(normalizedTerm)) return index;
    }
    return -1;
  };

  const revenueEnd = resolvedExpenseStart > revenueStart ? resolvedExpenseStart : headerRow.length;
  const publicRevenueColumn = findHeaderColumn("kamu", revenueStart, revenueEnd);
  const privateRevenueColumn = findHeaderColumn("ozel sektor", revenueStart, revenueEnd);
  const totalRevenueColumn = findHeaderColumn("toplam", revenueStart, revenueEnd);
  const personnelExpenseColumn = findHeaderColumn("personel gider", resolvedExpenseStart);
  const officeRentExpenseColumn = findHeaderColumn("ofis kira", resolvedExpenseStart);
  const operatingExpenseColumn = findHeaderColumn("isletme gider", resolvedExpenseStart);
  const universityExpenseColumn = findHeaderColumn("universite isbirligi", resolvedExpenseStart);
  const otherExpenseColumn = findHeaderColumn("diger gider", resolvedExpenseStart);
  const totalExpenseColumn = findHeaderColumn("toplam", resolvedExpenseStart);

  const endIndex = findRow(rows, "proje disi", headerIndex + 1);
  return rows
    .slice(headerIndex + 1, endIndex > headerIndex ? endIndex : undefined)
    .filter((row) => String(row[projectColumn] || "").trim())
    .map((row) => {
      const identity = parseProjectIdentity(row[projectColumn]);
      const publicRevenue = numberValue(row[publicRevenueColumn]);
      const privateRevenue = numberValue(row[privateRevenueColumn]);
      const personnelExpense = numberValue(row[personnelExpenseColumn]);
      const officeRentExpense = numberValue(row[officeRentExpenseColumn]);
      const operatingExpense = numberValue(row[operatingExpenseColumn]);
      const universityCooperationExpense = numberValue(row[universityExpenseColumn]);
      const otherExpense = numberValue(row[otherExpenseColumn]);
      return {
        status: statusColumn >= 0 ? String(row[statusColumn] || "").trim() : "Belirtilmemiş",
        ...identity,
        revenue: {
          public: publicRevenue,
          private: privateRevenue,
          total: numberValue(row[totalRevenueColumn]) || publicRevenue + privateRevenue
        },
        expense: {
          personnel: personnelExpense,
          officeRent: officeRentExpense,
          operating: operatingExpense,
          universityCooperation: universityCooperationExpense,
          other: otherExpense,
          total: numberValue(row[totalExpenseColumn]) || personnelExpense + officeRentExpense + operatingExpense + universityCooperationExpense + otherExpense
        }
      };
    });
};

const parseOffProject = (rows) => {
  const index = findRow(rows, "proje disi");
  if (index < 0) return { income: 0, expense: 0 };
  const row = rows[index];
  const labelColumn = findCellIndex(row, "proje disi");
  return { income: numberValue(row[labelColumn + 1]), expense: numberValue(row[labelColumn + 2]) };
};

const parseExemptions = (rows) => {
  const sectionIndex = findRow(rows, "muafiyetler");
  if (sectionIndex < 0) return [];
  const labels = {
    matrah: "Matrah",
    "hesaplanan tutar": "Hesaplanan Tutar",
    muafiyet: "Muafiyet"
  };
  return rows
    .slice(sectionIndex + 1, sectionIndex + 8)
    .map((row) => {
      const labelColumn = row.findIndex((cell) => Object.keys(labels).includes(normalizeFinancialText(cell)));
      const normalized = labelColumn >= 0 ? normalizeFinancialText(row[labelColumn]) : "";
      const key = Object.keys(labels).find((item) => normalized === item);
      if (!key) return null;
      return {
        key: key.replace(/\s+/g, "_"),
        label: labels[key],
        kdvSoftware: numberValue(row[labelColumn + 1]),
        incomeTax: numberValue(row[labelColumn + 2]),
        sgkEmployer: numberValue(row[labelColumn + 3])
      };
    })
    .filter(Boolean);
};

const parseTrade = (rows) => {
  const sectionIndex = findRow(rows, "proje ithalat ve ihracaat");
  if (sectionIndex < 0) return [];
  const fundIndex = findRow(rows, "arge fonlari", sectionIndex + 1);
  const headerIndex = rows.findIndex((row, index) => index > sectionIndex && index < (fundIndex > 0 ? fundIndex : rows.length) && rowContains(row, "ulke"));
  const countryColumn = headerIndex >= 0 ? findCellIndex(rows[headerIndex], "ulke") : 0;
  return rows
    .slice((headerIndex >= 0 ? headerIndex : sectionIndex + 1) + 1, fundIndex > sectionIndex ? fundIndex : undefined)
    .filter((row) => row.some((cell, index) => index >= countryColumn && index <= countryColumn + 4 && String(cell ?? "").trim()))
    .map((row) => ({
      country: String(row[countryColumn] || "").trim(),
      projectImport: numberValue(row[countryColumn + 1]),
      projectExport: numberValue(row[countryColumn + 2]),
      offProjectImport: numberValue(row[countryColumn + 3]),
      offProjectExport: numberValue(row[countryColumn + 4])
    }));
};

const parseFunds = (rows) => {
  const sectionIndex = findRow(rows, "arge fonlari");
  if (sectionIndex < 0) return { tubitak: 0, kosgeb: 0, ttgv: 0, eu: 0, total: 0 };

  const headerIndex = rows.findIndex((row, index) => index > sectionIndex && rowContains(row, "tubitak"));
  const firstValueColumn = headerIndex >= 0 ? findCellIndex(rows[headerIndex], "tubitak") : 1;
  const candidates = rows.slice((headerIndex >= 0 ? headerIndex : sectionIndex + 1) + 1, sectionIndex + 7);
  const totalRow = candidates.find((row) => normalizeFinancialText(row[firstValueColumn - 1]) === "toplam") || candidates[0] || [];
  const tubitak = numberValue(totalRow[firstValueColumn]);
  const kosgeb = numberValue(totalRow[firstValueColumn + 1]);
  const ttgv = numberValue(totalRow[firstValueColumn + 2]);
  const eu = numberValue(totalRow[firstValueColumn + 3]);
  return { tubitak, kosgeb, ttgv, eu, total: numberValue(totalRow[firstValueColumn + 4]) || tubitak + kosgeb + ttgv + eu };
};

const sumBy = (items, getter) => items.reduce((sum, item) => sum + numberValue(getter(item)), 0);

const buildSummary = ({ projects, offProject, exemptions, trade, funds }) => {
  const tradeTotal = trade.find((item) => normalizeFinancialText(item.country) === "toplam") || {
    projectImport: sumBy(trade, (item) => item.projectImport),
    projectExport: sumBy(trade, (item) => item.projectExport),
    offProjectImport: sumBy(trade, (item) => item.offProjectImport),
    offProjectExport: sumBy(trade, (item) => item.offProjectExport)
  };
  const exemption = exemptions.find((item) => item.key === "muafiyet") || {};

  return {
    projectRevenue: sumBy(projects, (item) => item.revenue.total),
    projectExpense: sumBy(projects, (item) => item.expense.total),
    offProjectIncome: offProject.income,
    offProjectExpense: offProject.expense,
    kdvExemption: numberValue(exemption.kdvSoftware),
    incomeTaxExemption: numberValue(exemption.incomeTax),
    sgkEmployerExemption: numberValue(exemption.sgkEmployer),
    projectImport: numberValue(tradeTotal.projectImport),
    projectExport: numberValue(tradeTotal.projectExport),
    offProjectImport: numberValue(tradeTotal.offProjectImport),
    offProjectExport: numberValue(tradeTotal.offProjectExport),
    fundSupport: numberValue(funds.total)
  };
};

export const parseMonthlyFinancialWorkbook = (workbook, fileName = "") => {
  const sheetName = workbook.SheetNames?.[0];
  if (!sheetName) throw new Error("Excel dosyasında okunabilir bir sayfa bulunamadı.");

  const sheet = workbook.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    raw: true,
    defval: ""
  }).map((row) => row.map(cleanCell));

  const projectHeaderIndex = rows.findIndex((row) => rowContains(row, "stb kodu"));
  const exemptionIndex = findRow(rows, "muafiyetler");
  if (projectHeaderIndex < 0 || exemptionIndex < 0) {
    throw new Error("Dosya, Argevio Aylık Excel Bilgi Formu yapısıyla eşleşmiyor.");
  }

  const detectedPeriod = detectPeriod(sheetName, fileName, rows);
  const projects = parseProjectRows(rows);
  const offProject = parseOffProject(rows);
  const exemptions = parseExemptions(rows);
  const trade = parseTrade(rows);
  const funds = parseFunds(rows);
  let detectedCompanyName = "";
  for (const row of rows.slice(0, 5)) {
    const companyLabelColumn = findCellIndex(row, "firma", true);
    if (companyLabelColumn >= 0) {
      detectedCompanyName = String(row.slice(companyLabelColumn + 1).find((cell) => String(cell ?? "").trim()) || "").trim();
      if (detectedCompanyName) break;
    }
  }

  return {
    schemaVersion: 1,
    sourceSheetName: sheetName,
    detectedCompanyName,
    detectedPeriod,
    projects,
    offProject,
    exemptions,
    trade,
    funds,
    summary: buildSummary({ projects, offProject, exemptions, trade, funds }),
    rowCount: rows.filter((row) => row.some((cell) => String(cell ?? "").trim())).length
  };
};

export const readMonthlyFinancialFile = async (file) => {
  if (!file) throw new Error("Excel dosyası seçilmedi.");
  const extension = String(file.name || "").toLowerCase();
  if (!extension.endsWith(".xlsx") && !extension.endsWith(".xls")) {
    throw new Error("Yalnızca .xlsx veya .xls dosyaları yüklenebilir.");
  }
  if (file.size > 15 * 1024 * 1024) throw new Error("Excel dosyası 15 MB’tan küçük olmalıdır.");

  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array", cellDates: true, cellFormula: true });
  return parseMonthlyFinancialWorkbook(workbook, file.name);
};

export const FINANCIAL_DATASETS = {
  monthly: {
    label: "Aylık mali göstergeler",
    dimensions: ["periodLabel", "companyName"],
    defaultMetrics: [
      "projectRevenue",
      "projectExpense",
      "offProjectIncome",
      "offProjectExpense",
      "kdvExemption",
      "incomeTaxExemption",
      "sgkEmployerExemption"
    ],
    metrics: {
      projectRevenue: "Proje geliri",
      projectExpense: "Proje gideri",
      offProjectIncome: "Proje dışı gelir",
      offProjectExpense: "Proje dışı gider",
      kdvExemption: "KDV yazılım istisnası",
      incomeTaxExemption: "Personel gelir vergisi muafiyeti",
      sgkEmployerExemption: "SGK işveren hissesi muafiyeti",
      projectImport: "Proje ithalatı",
      projectExport: "Proje ihracatı",
      offProjectImport: "Proje dışı ithalat",
      offProjectExport: "Proje dışı ihracat",
      fundSupport: "Ar-Ge fon desteği"
    }
  },
  projects: {
    label: "Proje mali detayları",
    dimensions: ["periodLabel", "companyName", "projectName", "projectCode", "status"],
    metrics: {
      publicRevenue: "Kamu geliri",
      privateRevenue: "Özel sektör geliri",
      totalRevenue: "Toplam proje geliri",
      personnelExpense: "Personel gideri",
      officeRentExpense: "Ofis kira gideri",
      operatingExpense: "İşletme gideri",
      universityCooperationExpense: "Üniversite işbirliği gideri",
      otherExpense: "Diğer giderler",
      totalExpense: "Toplam proje gideri"
    }
  },
  trade: {
    label: "Dış ticaret",
    dimensions: ["periodLabel", "companyName", "country"],
    metrics: {
      projectImport: "Proje ithalatı",
      projectExport: "Proje ihracatı",
      offProjectImport: "Proje dışı ithalat",
      offProjectExport: "Proje dışı ihracat"
    }
  }
};

export const FINANCIAL_DIMENSION_LABELS = {
  periodLabel: "Dönem",
  companyName: "Firma",
  projectName: "Proje",
  projectCode: "STB kodu",
  status: "Proje durumu",
  country: "Ülke"
};

export const buildFinancialDatasetRows = (reports, datasetKey) => {
  const base = (reports || []).flatMap((report) => {
    const common = {
      reportId: report.id,
      periodKey: report.periodKey,
      periodLabel: report.periodLabel,
      companyId: report.companyId,
      companyName: report.companyName
    };

    if (datasetKey === "projects") {
      return (report.projects || []).map((project) => ({
        ...common,
        projectName: project.projectName,
        projectCode: project.projectCode,
        status: project.status,
        publicRevenue: numberValue(project.revenue?.public),
        privateRevenue: numberValue(project.revenue?.private),
        totalRevenue: numberValue(project.revenue?.total),
        personnelExpense: numberValue(project.expense?.personnel),
        officeRentExpense: numberValue(project.expense?.officeRent),
        operatingExpense: numberValue(project.expense?.operating),
        universityCooperationExpense: numberValue(project.expense?.universityCooperation),
        otherExpense: numberValue(project.expense?.other),
        totalExpense: numberValue(project.expense?.total)
      }));
    }

    if (datasetKey === "trade") {
      return (report.trade || [])
        .filter((item) => normalizeFinancialText(item.country) !== "toplam")
        .map((item) => ({ ...common, ...item }));
    }

    return [{ ...common, ...(report.summary || {}) }];
  });

  return base;
};

export const createFinancialPivot = ({ rows, dimension, metrics, aggregation = "sum" }) => {
  const grouped = new Map();
  (rows || []).forEach((row) => {
    const label = String(row[dimension] || "Belirtilmemiş");
    if (!grouped.has(label)) {
      grouped.set(label, {
        label,
        sortValue: dimension === "periodLabel" ? String(row.periodKey || label) : label,
        values: {},
        counts: {}
      });
    }
    const group = grouped.get(label);
    metrics.forEach((metric) => {
      group.values[metric] = (group.values[metric] || 0) + numberValue(row[metric]);
      group.counts[metric] = (group.counts[metric] || 0) + 1;
    });
  });

  return Array.from(grouped.values())
    .sort((a, b) => a.sortValue.localeCompare(b.sortValue, "tr", { numeric: true }))
    .map((group) => ({
      label: group.label,
      ...Object.fromEntries(metrics.map((metric) => [
        metric,
        aggregation === "average" && group.counts[metric]
          ? group.values[metric] / group.counts[metric]
          : group.values[metric]
      ]))
    }));
};

export const formatFinancialPeriod = (periodKey) => {
  const [year, month] = String(periodKey || "").split("-").map(Number);
  return year && month ? `${MONTH_LABELS[month]} ${year}` : periodKey;
};
